/**
 * pages/report/systemHealth.ts · 系统健康监控面板 (admin)
 *
 * 一键看 v300 真生产健康:
 *   - 数据库表 + 行数统计
 *   - 错误日志 (近 7 天)
 *   - schema migration 状态
 *   - 部署版本 + 启动时间
 *   - 用户活跃度 (今日登录)
 */

import { query } from '@core/db';
import { getCurrentUser } from '@core/auth';
import { renderPageHeader, renderKPIGrid, type KPI } from '@widgets/KPICard';
import { renderDataTable, type Column } from '@widgets/DataTable';
import { getErrorStats, listErrors } from '@services/errorMonitoring';
import { APP_VERSION } from '@/constants/company';
import { fmtDateTime } from '@/shared/format';

export async function render(): Promise<HTMLElement> {
  const c = document.createElement('div');
  c.appendChild(renderPageHeader('🩺', '系统健康监控 · System Health', 'admin 一键看 v300 真生产状态'));

  const user = getCurrentUser();
  if (!user || !['admin', 'gm', 'boss'].includes(user.role)) {
    const err = document.createElement('div');
    err.className = 'bg-red-50 border border-red-200 p-4 rounded-lg';
    err.innerHTML = '🔒 仅 admin/gm/boss 可访问系统健康监控';
    c.appendChild(err);
    return c;
  }

  // 数据库表 + 行数统计
  const tables = ['employees', 'customers', 'suppliers', 'products', 'sales_orders',
                  'purchase_orders', 'work_orders', 'quotations', 'customer_payments',
                  'customer_invoices', 'qc_records', 'inventory', 'bom_items',
                  'audit_events', 'purchase_requisitions'];
  const dbStats: { table: string; count: number; size_kb: string }[] = [];
  let totalRows = 0;
  for (const t of tables) {
    const cnt = query<{ c: number }>(`SELECT COUNT(*) c FROM ${t}`)[0]?.c || 0;
    totalRows += cnt;
    dbStats.push({ table: t, count: cnt, size_kb: '~' });
  }

  // 错误统计
  const errStats = getErrorStats();

  // Schema migrations
  const migrations = query<{ id: string; applied_at: string }>(
    `SELECT id, applied_at FROM __tn_migrations ORDER BY id`
  );

  // 主 KPI
  const kpis: KPI[] = [
    { label: '数据表数', value: tables.length, color: 'blue' },
    { label: '总记录数', value: totalRows, color: 'green' },
    { label: '今日错误', value: errStats.today, color: errStats.today > 0 ? 'red' : 'green' },
    { label: '7 天错误', value: errStats.weekly, color: errStats.weekly > 5 ? 'orange' : 'green' },
    { label: 'Migrations', value: migrations.length, color: 'purple' },
    { label: 'v300 版本', value: APP_VERSION, color: 'cyan' },
  ];
  c.appendChild(renderKPIGrid(kpis));

  // 数据库统计表
  const dbCard = document.createElement('div');
  dbCard.className = 'bg-white rounded-lg shadow-sm border border-gray-200 p-5 mt-4';
  dbCard.innerHTML = '<h2 class="text-base font-bold mb-3">💾 数据库表统计 (IndexedDB sql.js)</h2>';
  const dbCols: Column<typeof dbStats[0]>[] = [
    { key: 'table', label: '表名' },
    { key: 'count', label: '记录数', align: 'right', render: r => `<span class="font-semibold ${r.count > 0 ? 'text-green-700' : 'text-gray-400'}">${r.count}</span>` },
  ];
  const dbTable = document.createElement('div');
  dbCard.appendChild(dbTable);
  dbTable.appendChild(renderDataTable({ columns: dbCols, data: dbStats, emptyText: '无表数据' }));
  c.appendChild(dbCard);

  // 错误日志
  const errorList = listErrors(20);
  if (errorList.length > 0) {
    const errCard = document.createElement('div');
    errCard.className = 'bg-white rounded-lg shadow-sm border border-red-200 p-5 mt-4';
    errCard.innerHTML = '<h2 class="text-base font-bold mb-3 text-red-700">🚨 近 20 条错误日志</h2>';
    const errCols: Column<typeof errorList[0]>[] = [
      { key: 'created_at', label: '时间', width: '180px', render: (r) => fmtDateTime(r.created_at) },
      { key: 'level', label: '级别', width: '80px', render: r => `<span class="inline-block px-2 py-0.5 rounded text-xs ${r.level === 'error' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}">${r.level}</span>` },
      { key: 'actor_role', label: '角色', width: '100px' },
      { key: 'message', label: '错误信息' },
    ];
    errCard.appendChild(renderDataTable({ columns: errCols, data: errorList, emptyText: '✅ 暂无错误' }));
    c.appendChild(errCard);
  } else {
    const ok = document.createElement('div');
    ok.className = 'bg-green-50 border border-green-200 p-4 rounded-lg mt-4 text-center';
    ok.innerHTML = '<div class="text-3xl mb-2">✅</div><div class="text-green-800 font-semibold">系统运行正常 · 无错误记录</div>';
    c.appendChild(ok);
  }

  // Schema migrations
  const migCard = document.createElement('div');
  migCard.className = 'bg-white rounded-lg shadow-sm border border-gray-200 p-5 mt-4';
  migCard.innerHTML = '<h2 class="text-base font-bold mb-3">🔄 Schema Migrations 状态</h2>';
  const migCols: Column<typeof migrations[0]>[] = [
    { key: 'id', label: 'Migration ID' },
    { key: 'applied_at', label: '应用时间', width: '220px' },
  ];
  migCard.appendChild(renderDataTable({ columns: migCols, data: migrations, emptyText: '尚未应用 migration' }));
  c.appendChild(migCard);

  // 浏览器信息
  const browserCard = document.createElement('div');
  browserCard.className = 'bg-blue-50 border border-blue-200 p-4 rounded-lg mt-4';
  browserCard.innerHTML = `
    <h3 class="font-bold mb-2 text-blue-900">🌐 浏览器/环境</h3>
    <div class="text-xs text-gray-700 space-y-1">
      <div><strong>User Agent:</strong> ${navigator.userAgent.substring(0, 120)}</div>
      <div><strong>Language:</strong> ${navigator.language}</div>
      <div><strong>Screen:</strong> ${window.screen.width}×${window.screen.height}</div>
      <div><strong>Viewport:</strong> ${window.innerWidth}×${window.innerHeight}</div>
      <div><strong>Online:</strong> ${navigator.onLine ? '✅ 在线' : '❌ 离线'}</div>
      <div><strong>Device:</strong> ${/Mobile|Android|iPhone/i.test(navigator.userAgent) ? '📱 移动端' : '🖥️ 桌面端'}</div>
    </div>
  `;
  c.appendChild(browserCard);

  return c;
}
